/**
 * 화면 상태(고른 기관·필터·검색어·기간·보기 방식·페이지) 기억 저장소.
 *
 * 사이드바로 다른 화면에 갔다 오거나 새로고침해도 방금 보던 조건이 그대로
 * 남아 있게 한다. 관리자 콘솔과 기관 포털이 함께 쓴다 — 그래서 어느 한쪽 번들에
 * 속하지 않는 `@/utils` 에 둔다. React 를 모르는 순수 함수만 둔 이유는 로그아웃
 * 경로(훅 바깥)에서도 `clearUiState()` 를 불러야 하기 때문이다. 훅은
 * `@/hooks/usePersistentState` 에 있다.
 *
 * 키 형식: `vnc:ui:<계정 id>:<화면 키>:<필드>`
 *  - 계정별로 나눈다. 기관 PC 한 대를 담당자 여럿이 번갈아 쓰는 곳이 있어서,
 *    나눠 두지 않으면 앞사람이 고른 기관·검색어가 다음 사람 화면에 뜬다.
 *  - 로그아웃하면 접두사째 지운다(`clearUiState`). 계정으로 나눴어도 남겨 둘
 *    이유가 없고, 공용 PC 에 남의 검색어를 쌓아 두지 않기 위해서다.
 *
 * 저장하지 않는 것: 열린 모달·편집 모드·선택(일괄 처리) 모드·입력 중인 폼·확인
 * 문구·토스트. 돌아왔을 때 모달이 다시 열려 있거나 반쯤 쓴 폼이 남아 있으면
 * "무엇을 하다 말았는지" 를 다시 따져야 해서 오히려 방해가 된다.
 */
const PREFIX = 'vnc:ui:';

/**
 * 저장된 값을 화면이 쓸 수 있는 값으로 되돌린다. 쓸 수 없으면 undefined —
 * 그때는 화면의 기본값을 쓴다. localStorage 에는 옛 형식·남이 손댄 값이 들어
 * 있을 수 있으므로 저장된 값을 그대로 믿지 않는다.
 */
export type UiStateParser<T> = (raw: unknown) => T | undefined;

export const uiStateKey = (accountId: string, pageKey: string, field: string) =>
  `${PREFIX}${accountId}:${pageKey}:${field}`;

/**
 * localStorage 는 없을 수도, 막혀 있을 수도 있다(사파리 프라이빗 모드·용량 초과).
 * 기억이 안 될 뿐 화면이 서면 안 되므로 모든 접근을 여기서 삼킨다.
 */
export const readUiState = <T>(key: string, parse: UiStateParser<T>): T | undefined => {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return undefined;
    return parse(JSON.parse(raw));
  } catch {
    return undefined;
  }
};

export const writeUiState = (key: string, value: unknown): void => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // 저장이 안 되면 다음 방문에 기본값으로 돌아갈 뿐이다
  }
};

export const removeUiState = (key: string): void => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // 위와 같은 이유로 조용히 넘어간다
  }
};

/** 로그아웃 때 부른다. 모든 계정의 화면 기억을 지운다 */
export const clearUiState = (): void => {
  try {
    const storage = window.localStorage;
    const doomed: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key?.startsWith(PREFIX)) doomed.push(key);
    }
    // 돌면서 지우면 인덱스가 밀린다. 모은 뒤에 지운다
    for (const key of doomed) storage.removeItem(key);
  } catch {
    // 지우지 못해도 계정별로 나뉘어 있어 다음 계정에 섞이지는 않는다
  }
};

// ── 자주 쓰는 해석기 ────────────────────────────────────────────────────

export const parseString: UiStateParser<string> = (raw) =>
  typeof raw === 'string' ? raw : undefined;

export const parseBoolean: UiStateParser<boolean> = (raw) =>
  typeof raw === 'boolean' ? raw : undefined;

/** 1 이상의 정수. 페이지 번호·연도에 쓴다 */
export const parsePositiveInt: UiStateParser<number> = (raw) =>
  typeof raw === 'number' && Number.isInteger(raw) && raw >= 1 ? raw : undefined;

/** 정해진 값 중 하나. 선택지가 바뀌어 사라진 값이면 기본값으로 돌린다 */
export const parseOneOf =
  <T extends string | number>(values: readonly T[]): UiStateParser<T> =>
  (raw) =>
    values.includes(raw as T) ? (raw as T) : undefined;

/** `YYYY-MM-DD`. 날짜 입력(`<input type='date'>`)이 쓰는 형식 */
export const parseDateString: UiStateParser<string> = (raw) =>
  typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : undefined;

/** 문자열 배열. `allowed` 를 주면 그 안에 있는 값만 남긴다 */
export const parseStringArray =
  (allowed?: readonly string[]): UiStateParser<string[]> =>
  (raw) => {
    if (!Array.isArray(raw) || !raw.every((item) => typeof item === 'string')) return undefined;
    return allowed ? raw.filter((item) => allowed.includes(item)) : raw;
  };

/**
 * 기억해 둔 id 가 지금 목록에 아직 있는지 확인한다.
 *
 * 기관·키오스크가 지워졌는데 그 id 를 그대로 쓰면 빈 셀렉트와 빈 목록이 뜬다.
 * 목록이 아직 안 왔으면(undefined) 판단을 미루고 기억한 값을 그대로 쓴다 —
 * 그 사이 기본값으로 바꿔 버리면 목록이 도착하는 순간 기억이 지워진다.
 * 빈 값('')·`keep` 에 든 값(전체 기관 같은 표식)은 목록과 비교하지 않는다.
 */
export const pickKnownId = (
  stored: string,
  list: readonly { id: string }[] | undefined,
  fallback: string,
  keep: readonly string[] = [],
): string => {
  if (!stored || keep.includes(stored) || !list) return stored;
  return list.some((item) => item.id === stored) ? stored : fallback;
};
